const URL = process.env.VUE_APP_BACKEND_BASE_WS_URL

let socket = null

/**
 * WebSocket Connection States
 * Similar to Flutter's WebSocketConnectionState enum
 */
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
}

let connectionState = ConnectionState.DISCONNECTED
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_DELAY_BASE = 1000 // 1 second base delay
let reconnectTimeout = null

/**
 * Get current WebSocket connection state
 * @returns {string} Current connection state
 */
export function getConnectionState () {
  return connectionState
}

/**
 * Check if WebSocket is currently connected and ready
 * @returns {boolean} True if connected and ready to send messages
 */
export function isConnected () {
  return connectionState === ConnectionState.CONNECTED
}

/**
 * Set connection state and notify listeners
 * @param {string} newState - New connection state
 */
function setConnectionState (newState) {
  const oldState = connectionState
  connectionState = newState
  console.debug(`[WebSocket] State transition: ${oldState} → ${newState}`)
}

export async function start (callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, device, client) {
  // Clear any pending reconnect timeout
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }

  document.cookie = `access_token=${window.sessionStorage.getItem('ACCESS_TOKEN')}; path=/`

  if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
    setConnectionState(ConnectionState.CONNECTING)

    try {
      socket = new WebSocket(URL)

      socket.addEventListener('open', () => {
        setConnectionState(ConnectionState.CONNECTED)
        reconnectAttempts = 0 // Reset reconnect attempts on successful connection

        if (socket.readyState === WebSocket.OPEN) {
          const subscriptionMessage = JSON.stringify({
            action: 'subscribe',
            deviceID: device,
            clientID: client,
            access_token: window.sessionStorage.getItem('ACCESS_TOKEN')
          })
          socket.send(subscriptionMessage)
          console.debug('[WebSocket] Connected and subscribed successfully')
        }
      })

      setInterval(async () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          await socket.send(JSON.stringify({
            action: 'keep_open',
            access_token: window.sessionStorage.getItem('ACCESS_TOKEN')
          }))
        }
      }, 15000)

      socket.addEventListener('close', () => {
        const previousState = connectionState
        setConnectionState(ConnectionState.DISCONNECTED)
        socket = null

        callbackWebsocketClosed()

        // Attempt to reconnect if we were previously connected
        if (previousState === ConnectionState.CONNECTED || previousState === ConnectionState.RECONNECTING) {
          attemptReconnect(callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, device, client)
        }
      })

      socket.addEventListener('error', (error) => {
        setConnectionState(ConnectionState.ERROR)
        console.error('[WebSocket] Connection error:', error)
        callbackError(error)
      })

      socket.addEventListener('message', (e) => {
        const data = JSON.parse(e.data)
        if (!data.message || data.message !== 'Ok') {
          if (data.action === 'notification_request') {
            callbackRequest(data.request)
          } else if (data.action === 'notification_response') {
            callbackResponse(data)
          } else if (data.status === 'error') {
            callbackError(data)
          }
        }
      })
    } catch (error) {
      setConnectionState(ConnectionState.ERROR)
      console.error('[WebSocket] Failed to create connection:', error)
      callbackError(error)

      // Attempt to reconnect
      attemptReconnect(callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, device, client)
    }
  }
}

/**
 * Attempt to reconnect WebSocket with exponential backoff
 * @param {Function} callbackError - Error callback
 * @param {Function} callbackRequest - Request callback
 * @param {Function} callbackResponse - Response callback
 * @param {Function} callbackWebsocketClosed - Closed callback
 * @param {string} device - Device ID
 * @param {string} client - Client ID
 */
function attemptReconnect (callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, device, client) {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error('[WebSocket] Max reconnection attempts reached. Please refresh the page.')
    setConnectionState(ConnectionState.ERROR)
    return
  }

  reconnectAttempts++
  setConnectionState(ConnectionState.RECONNECTING)

  // Exponential backoff: 1s, 2s, 4s, 8s, 16s
  const delay = RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttempts - 1)

  console.debug(`[WebSocket] Attempting reconnection ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`)

  reconnectTimeout = setTimeout(() => {
    console.debug('[WebSocket] Reconnecting...')
    start(callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, device, client)
  }, delay)
}

export async function notificationRequest (requestID, clientID, geoLocation) {
  if (!isConnected() || !socket || socket.readyState !== WebSocket.OPEN) {
    throw new Error('WebSocket is not connected. Please wait for connection to establish.')
  }

  return await socket.send(JSON.stringify({
    action: 'notification_request',
    requestID,
    clientID,
    clientGeoLocation: geoLocation,
    access_token: window.sessionStorage.getItem('ACCESS_TOKEN')
  }))
}

export async function notificationDecision (requestID, clientID, approval) {
  if (!isConnected() || !socket || socket.readyState !== WebSocket.OPEN) {
    throw new Error('WebSocket is not connected. Please wait for connection to establish.')
  }

  return await socket.send(JSON.stringify({
    action: 'notification_response',
    requestID,
    clientID,
    approved: approval,
    access_token: window.sessionStorage.getItem('ACCESS_TOKEN')
  }))
}

/**
 * Close WebSocket connection gracefully
 */
export function close () {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }

  if (socket) {
    setConnectionState(ConnectionState.DISCONNECTED)
    socket.close()
    socket = null
  }

  reconnectAttempts = 0
}
